import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { fetchLeads } from '../../../utils/api/Lead/getLeads';
import { ThemeContext } from '../../../context/ThemeContext';
import { LangContext } from '../../../context/LangContext';
import { LoadingCircle } from '../../UI/Custom/LoadingCircle';

import styles from './LeadPaginator.module.css';

const STORAGE_KEY = (role) => `leadNavState_${role}`;

const translations = {
    ru: { prev: 'Предыдущий лид', next: 'Следующий лид', page: (p, lp) => `Страница ${p}/${lp}` },
    en: { prev: 'Previous lead', next: 'Next lead', page: (p, lp) => `Page ${p}/${lp}` },
};

const safeRead = (role) => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY(role));
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const safeWrite = (role, payload) => {
    try {
        sessionStorage.setItem(STORAGE_KEY(role), JSON.stringify(payload));
    } catch (e) {
        // ignore
    }
};

export const LeadPaginator = ({ currentLeadId, role, className = '' }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { theme } = useContext(ThemeContext);
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;

    const [nav, setNav] = useState(null);

    // UX: loader while switching lead inside same page
    const [isNavigating, setIsNavigating] = useState(false);

    // UX + safety: loader + block while we fetch other page IDs
    const [isFetchingPage, setIsFetchingPage] = useState(false);

    // Safety: drop stale fetchLeads() results if they race (rare, but cheap to guard)
    const pageFetchSeqRef = useRef(0);

    const currentId = useMemo(() => Number(currentLeadId || 0), [currentLeadId]);

    useEffect(() => {
        setNav(safeRead(role));
    }, [role, location.key]);

    // stop “navigating” spinner once URL param changed
    useEffect(() => {
        if (isNavigating) setIsNavigating(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentId]);

    const indexInPage = useMemo(() => {
        if (!nav?.pageLeadIds?.length || !currentId) return -1;
        return nav.pageLeadIds.indexOf(currentId);
    }, [nav, currentId]);

    // ✅ page-local counter: 1/15 (NOT global 600/551)
    const positionText = useMemo(() => {
        if (!nav || indexInPage < 0) return '';
        const pageTotal = nav.pageLeadIds.length;
        return `${indexInPage + 1} / ${pageTotal}`;
    }, [nav, indexInPage]);

    const canPrev = useMemo(() => {
        if (!nav || indexInPage < 0) return false;
        return indexInPage > 0;
    }, [nav, indexInPage]);

    const canNext = useMemo(() => {
        if (!nav || indexInPage < 0) return false;
        return indexInPage < nav.pageLeadIds.length - 1;
    }, [nav, indexInPage]);

    const loadPage = useCallback(async (page) => {
        if (!nav) return null;

        const seq = ++pageFetchSeqRef.current;
        const result = await fetchLeads(page, nav.params, nav.per_page, role);

        // ignore stale
        if (seq !== pageFetchSeqRef.current) return null;

        const { data, meta } = result;

        const nextNav = {
            ...nav,
            page: meta.current_page,
            per_page: meta.per_page,
            meta,
            pageLeadIds: Array.isArray(data) ? data.map((l) => l.id) : [],
            savedAt: Date.now(),
        };

        setNav(nextNav);
        safeWrite(role, nextNav);

        return nextNav;
    }, [nav, role]);

    const goPrev = useCallback(async () => {
        if (!nav || !canPrev) return;

        // inside same page: just navigate, show spinner while route changes
        if (indexInPage > 0) {
            setIsNavigating(true);
            navigate(`/lead/change/info/${nav.pageLeadIds[indexInPage - 1]}`);
            return;
        }

        // need previous page
        if (isFetchingPage) return;
        setIsFetchingPage(true);

        try {
            const nextNav = await loadPage(nav.page - 1);
            const ids = nextNav?.pageLeadIds || [];
            if (ids.length) {
                setIsNavigating(true);
                navigate(`/lead/change/info/${ids[ids.length - 1]}`);
            }
        } finally {
            setIsFetchingPage(false);
        }
    }, [nav, canPrev, indexInPage, isFetchingPage, loadPage, navigate]);

    const goNext = useCallback(async () => {
        if (!nav || !canNext) return;

        // inside same page: just navigate, show spinner while route changes
        if (indexInPage >= 0 && indexInPage < nav.pageLeadIds.length - 1) {
            setIsNavigating(true);
            navigate(`/lead/change/info/${nav.pageLeadIds[indexInPage + 1]}`);
            return;
        }

        // need next page
        if (isFetchingPage) return;
        setIsFetchingPage(true);

        try {
            const nextNav = await loadPage(nav.page + 1);
            const ids = nextNav?.pageLeadIds || [];
            if (ids.length) {
                setIsNavigating(true);
                navigate(`/lead/change/info/${ids[0]}`);
            }
        } finally {
            setIsFetchingPage(false);
        }
    }, [nav, canNext, indexInPage, isFetchingPage, loadPage, navigate]);

    // opened directly (no list context) OR lead not in saved IDs => hide
    if (!nav || indexInPage < 0) return null;

    const lastPage = Number(nav.meta?.last_page || nav.page);
    const isBusy = isFetchingPage || isNavigating;

    return (
        <div className={`${styles.wrap} ${theme === 'dark' ? styles.dark : ''} ${className}`}>
            <button
                type="button"
                className={styles.btn}
                onClick={goPrev}
                disabled={!canPrev || isFetchingPage}
                title={t.prev}
            >
                <ChevronLeftIcon fontSize="small" />
            </button>

            <div className={styles.counter} title={t.page(nav.page, lastPage)}>
                {isBusy ? <LoadingCircle size={18} thickness={2} /> : positionText}
            </div>

            <button
                type="button"
                className={styles.btn}
                onClick={goNext}
                disabled={!canNext || isFetchingPage}
                title={t.next}
            >
                <ChevronRightIcon fontSize="small" />
            </button>
        </div>
    );
};
